import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../baseQueryWithReauth";
import { serializeArgsWithoutToken } from "../serializeQueryArgs";

/**
 * EMQX Cloud admin proxy API.
 * Server endpoint'leri /api/emqx/storageRoles/* — admin role onerisi.
 */
const emqxApi = createApi({
  reducerPath: "emqx",
  baseQuery: baseQueryWithReauth,
  serializeQueryArgs: serializeArgsWithoutToken, // #142: token önbellek anahtarına girmez
  keepUnusedDataFor: 30,
  tagTypes: ["EmqxClient", "EmqxUser", "EmqxAcl"],
  endpoints(builder) {
    return {
      // ─── Clients ───
      getEmqxClients: builder.query({
        query: ({ token, page = 1, limit = 50, clientid, username }) => {
          const params = new URLSearchParams();
          params.set("page", String(page));
          params.set("limit", String(limit));
          if (clientid) params.set("clientid", clientid);
          if (username) params.set("username", username);
          return {
            url: `/api/emqx/storageRoles/clients?${params.toString()}`,
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          };
        },
        providesTags: ["EmqxClient"],
      }),

      // ─── Users ───
      getEmqxUsers: builder.query({
        query: ({ token, page = 1, limit = 50, like_user_id }) => {
          const params = new URLSearchParams();
          params.set("page", String(page));
          params.set("limit", String(limit));
          if (like_user_id) params.set("like_user_id", like_user_id);
          return {
            url: `/api/emqx/storageRoles/users?${params.toString()}`,
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          };
        },
        providesTags: ["EmqxUser"],
      }),
      addEmqxUser: builder.mutation({
        query: ({ token, user_id, password }) => ({
          url: `/api/emqx/storageRoles/users`,
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: { user_id, password },
        }),
        invalidatesTags: ["EmqxUser"],
      }),
      updateEmqxUserPassword: builder.mutation({
        query: ({ token, user_id, password }) => ({
          url: `/api/emqx/storageRoles/users/${encodeURIComponent(user_id)}/password`,
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
          body: { password },
        }),
        invalidatesTags: ["EmqxUser"],
      }),
      deleteEmqxUser: builder.mutation({
        query: ({ token, user_id }) => ({
          url: `/api/emqx/storageRoles/users/${encodeURIComponent(user_id)}`,
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }),
        invalidatesTags: ["EmqxUser"],
      }),

      // ─── ACL ───
      getDeviceMqttUser: builder.query({
        query: ({ token, serialNo }) => ({
          url: `/api/emqx/storageRoles/device-mqtt-user/${encodeURIComponent(serialNo)}`,
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }),
      }),
      getUserAcl: builder.query({
        query: ({ token, username }) => ({
          url: `/api/emqx/storageRoles/users/${encodeURIComponent(username)}/acl`,
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        }),
        providesTags: ["EmqxAcl"],
      }),
      resetUserAcl: builder.mutation({
        query: ({ token, username, password, passwordConfirm, code }) => ({
          url: `/api/emqx/storageRoles/users/${encodeURIComponent(username)}/acl`,
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
          // Prod'da controlPassword+2FA ister; dev/stage'de yok sayılır.
          body: { password, passwordConfirm, ...(code ? { code } : {}) },
        }),
        invalidatesTags: ["EmqxAcl"],
      }),
      applyDeviceAcl: builder.mutation({
        query: ({ token, username, serialNo, password, passwordConfirm, code }) => ({
          url: `/api/emqx/storageRoles/users/${encodeURIComponent(username)}/acl`,
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
          body: { serialNo, password, passwordConfirm, ...(code ? { code } : {}) },
        }),
        invalidatesTags: ["EmqxAcl"],
      }),
    };
  },
});

export const {
  useGetEmqxClientsQuery,
  useLazyGetEmqxClientsQuery,
  useGetEmqxUsersQuery,
  useLazyGetEmqxUsersQuery,
  useAddEmqxUserMutation,
  useUpdateEmqxUserPasswordMutation,
  useDeleteEmqxUserMutation,
  useLazyGetDeviceMqttUserQuery,
  useGetUserAclQuery,
  useLazyGetUserAclQuery,
  useResetUserAclMutation,
  useApplyDeviceAclMutation,
} = emqxApi;

export { emqxApi };
